// Drives uploading (convert→upload→thumbnails) and continue (global generation hand-off).
// Reads/writes the file-to-storydoc store. Status + errors go to the store.
import { track } from "@/features/analytics";
import { uploadFile } from "@/providers/rendering-provider/utils/upload-file";
import { useLazyGetConvertApiTokenQuery } from "@api/media";
import { environment } from "@environment";
import { useAccountStore } from "@features/account-management";
import { useGetUploadFileTokenMutation } from "@/shared/services/pages/pages.mutation";
import { useGetCurrentUserQuery } from "@/shared/services/users/users.query";
import {
  PDF_MIME_TYPE,
  convertFileToPdf,
  isPdfFile,
} from "@/pages/home/utils/convert-file-to-pdf";
import { storydocThemes } from "@/pages/editor/modules/design-system/modules/theme/data/storydoc-themes";
import { renderPdfThumbnails } from "../utils/render-pdf-thumbnails";
import { selectSelectedPageNumbers, useFileToStorydocStore } from "../stores";
import {
  type ImportSource,
  type ImportStore,
  deckImportStore,
} from "../stores/file-to-storydoc-generation.store";
import {
  CAP_MESSAGE,
  countActiveImports,
  getViewingImport,
  isUploadOwnedByImport,
  selectViewing,
  useImportRegistry,
} from "../stores/import-registry.store";
import {
  closeImportDialog,
  isImportDialogOpen,
} from "../utils/import-dialog-id";
import { isSameImportRequest } from "../utils/is-same-import-request";
import {
  DRIVE_ACCESS_MESSAGE,
  DRIVE_FETCH_MESSAGE,
  DriveImportError,
  type PickedDriveFile,
  downloadDriveFile,
} from "../utils/pick-drive-file";
import { trackImport } from "../utils/track-import";
import {
  MAX_IMPORT_FILE_BYTES,
  OVERSIZED_MESSAGE,
} from "../utils/validate-import-file";
import { useCallback, useRef } from "react";
import { toast } from "react-toastify";

const revokeThumbnails = (pages: { thumbnailUrl: string }[]) => {
  for (const p of pages) {
    if (p.thumbnailUrl.startsWith("blob:")) URL.revokeObjectURL(p.thumbnailUrl);
  }
};

const getPdfUploadFilename = (userId: string, orgId: string) =>
  `${orgId}/pdf-to-storydoc/${userId}:${new Date().getTime()}.pdf`;

export const useFileToStorydocFlow = () => {
  const orgId = useAccountStore((s) => s._id);
  const { data: user } = useGetCurrentUserQuery();
  const { mutateAsync: getUploadFileToken } = useGetUploadFileTokenMutation();
  const [getConvertApiToken] = useLazyGetConvertApiTokenQuery();
  const abortRef = useRef<AbortController | null>(null);
  const continuingRef = useRef(false);

  const store = useFileToStorydocStore;

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    closeImportDialog();
  }, []);

  const beginRun = useCallback(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    return controller;
  }, []);

  const runUpload = useCallback(
    async (
      file: File,
      source: ImportSource,
      controller: AbortController,
      from = 0,
    ) => {
      const s = store.getState();
      const at = (x: number) =>
        store.getState().setUploadProgress(from + (1 - from) * x);
      at(0.05);
      useImportRegistry.getState().setViewingId(null);
      s.setReturnedFromVersions(false);
      s.setStep("uploading");
      s.setOriginalFile(file);
      s.setSource(source);
      s.setStatusMessage("Uploading your file...");
      try {
        if (!user) throw new Error("User not found");
        if (!orgId) throw new Error("Organization not found");
        let pdfFile = file;
        if (!isPdfFile(file)) {
          const { token } = await getConvertApiToken({}).unwrap();
          if (controller.signal.aborted) return;
          at(0.1);
          pdfFile = await convertFileToPdf(file, token, controller.signal);
          if (controller.signal.aborted) return;
          at(0.4);
          if (pdfFile.size > MAX_IMPORT_FILE_BYTES) {
            store.getState().setError(OVERSIZED_MESSAGE);
            trackImport("error", {
              source,
              importJobId: null,
              stage: "convert",
              code: "size",
            });
            return;
          }
        }
        s.setPdfFile(pdfFile);
        const uploadToken = await getUploadFileToken({
          filename: getPdfUploadFilename(user._id, orgId),
          contentType: PDF_MIME_TYPE,
          typeStory: false,
        });
        if (controller.signal.aborted) return;
        at(isPdfFile(file) ? 0.15 : 0.45);
        s.setPdfUrl(
          `https://${environment.assetsBucket}/${uploadToken.url.fields.key}`,
        );
        try {
          await uploadFile(uploadToken.url, pdfFile, {
            signal: controller.signal,
          });
        } catch (e) {
          if (controller.signal.aborted) throw e;
          // dev bucket 403/CORS — proceed (backend re-fetch surfaces real errors)
        }
        at(0.7);
        const urls = await renderPdfThumbnails(pdfFile, {
          signal: controller.signal,
          onPage: (done, total) => at(0.7 + (0.3 * done) / total),
        });
        if (controller.signal.aborted) return;
        const previous = store.getState();
        if (!isUploadOwnedByImport(previous.pdfUrl))
          revokeThumbnails(previous.pages);
        s.setPages(
          urls.map((thumbnailUrl, i) => ({
            pageNumber: i + 1,
            thumbnailUrl,
            selected: true,
          })),
        );
        s.setStep("selecting");
        track("file to storydoc uploaded", { pages: urls.length });
        trackImport("file uploaded", {
          source,
          importJobId: null,
          pages: urls.length,
          fileType: file.name.split(".").pop() ?? "",
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        store
          .getState()
          .setError(
            "We couldn't read this file. Try exporting it again and re-uploading.",
          );
        track("failed process file to storydoc", {
          error: (error as Error)?.message,
        });
      }
    },
    [getConvertApiToken, getUploadFileToken, orgId, store, user],
  );

  const startUpload = useCallback(
    (file: File, source: ImportSource = "local") =>
      runUpload(file, source, beginRun()),
    [beginRun, runUpload],
  );

  const startDriveUpload = useCallback(
    async (picked: PickedDriveFile) => {
      const controller = beginRun();
      useImportRegistry.getState().setViewingId(null);
      const s = store.getState();
      s.setStep("uploading");
      s.setOriginalFile(null);
      s.setSource("drive");
      s.setInlineError(null);
      s.setStatusMessage("Uploading your file...");
      s.setUploadProgress(0.02);
      let file: File;
      try {
        file = await downloadDriveFile(picked, controller.signal);
      } catch (error) {
        if (controller.signal.aborted) return;
        const driveError =
          error instanceof DriveImportError
            ? error
            : new DriveImportError("fetch", "drive-download", "unknown");
        s.setStep("source");
        s.setInlineError(
          driveError.code === "access"
            ? DRIVE_ACCESS_MESSAGE
            : DRIVE_FETCH_MESSAGE,
          "drive",
        );
        trackImport("error", {
          source: "drive",
          importJobId: null,
          stage: driveError.stage,
          code: driveError.detail,
        });
        return;
      }
      if (controller.signal.aborted) return;
      if (file.size > MAX_IMPORT_FILE_BYTES) {
        s.setStep("source");
        s.setInlineError(OVERSIZED_MESSAGE);
        trackImport("error", {
          source: "drive",
          importJobId: null,
          stage: "drive-download",
          code: "size",
        });
        return;
      }
      await runUpload(file, "drive", controller, 0.25);
    },
    [beginRun, runUpload, store],
  );

  // The page thumbnails are blob: URLs that the dialog revokes on close; the
  // layout picker shows them minutes later, so they travel as data URLs.
  const toDataUrl = async (url: string) => {
    if (!url.startsWith("blob:")) return url;
    try {
      const blob = await (await fetch(url)).blob();
      return await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => resolve("");
        reader.readAsDataURL(blob);
      });
    } catch {
      return "";
    }
  };

  const continueToGeneration = useCallback(async () => {
    const s = store.getState();
    const pages = selectSelectedPageNumbers(s.pages);
    if (!s.pdfUrl || pages.length === 0) return;
    if (continuingRef.current) return;
    continuingRef.current = true;
    try {
      const baseName =
        (s.originalFile?.name ?? "Untitled").replace(/\.[^.]+$/, "").trim() ||
        "Untitled";
      // Only account design systems exist in the DB. Storydoc presets are
      // color-only (not stored), so their id must NOT be sent as designSystemId —
      // the preset's colors are applied FE-side via resolveDesignSystem.
      // selectedDesignSystemId still rides along so that resolution can match it.
      const isPreset = storydocThemes.some(
        (p) => p.id === s.selectedDesignSystemId,
      );
      const designSystemId =
        s.selectedDesignSystemId && !isPreset
          ? s.selectedDesignSystemId
          : undefined;

      const registry = useImportRegistry.getState();
      const isDeck = s.target === "deck";
      const viewing = isDeck ? null : selectViewing(registry);
      const viewingState = viewing?.store.getState();
      const ownImport =
        viewing &&
        viewingState?.request?.pdfUrl === s.pdfUrl &&
        viewingState.createdStory == null &&
        viewingState.status !== "error"
          ? viewing
          : null;
      if (
        ownImport &&
        isSameImportRequest(
          {
            pdfUrl: s.pdfUrl,
            pages,
            selectedDesignSystemId: s.selectedDesignSystemId,
          },
          ownImport.store.getState(),
        )
      ) {
        trackImport(
          "continue",
          {
            selectedPages: pages.length,
            themed: !!s.selectedDesignSystemId,
            resumed: true,
            concurrentImports: countActiveImports(registry.imports),
          },
          ownImport.store,
        );
        ownImport.store.getState().setCompleteToastDismissed(false);
        ownImport.store.getState().setProgressToastDismissed(false);
        closeImportDialog();
        return;
      }

      const pageThumbnails = await Promise.all(
        s.pages.filter((p) => p.selected).map((p) => toDataUrl(p.thumbnailUrl)),
      );
      if (!isImportDialogOpen()) return;
      let jobStore: ImportStore;
      if (isDeck) {
        jobStore = deckImportStore;
      } else {
        const entry = ownImport ?? useImportRegistry.getState().addImport();
        if (!entry) {
          toast.error(CAP_MESSAGE);
          return;
        }
        jobStore = entry.store;
      }
      jobStore.getState().startJob({
        request: {
          pdfUrl: s.pdfUrl,
          pages,
          ...(designSystemId ? { designSystemId } : {}),
          ...(s.selectedDesignSystemId ? { themed: true } : {}),
        },
        selectedDesignSystemId: s.selectedDesignSystemId,
        keepDeckTheme: s.keepDeckTheme,
        themeOnly: s.themeOnly,
        title: baseName,
        fileName: s.originalFile?.name ?? `${baseName}.pdf`,
        source: s.source,
        entryPoint: s.entryPoint,
        target: s.target,
        pageThumbnails,
        ...(isDeck
          ? {}
          : {
              upload: {
                originalFile: s.originalFile,
                pdfFile: s.pdfFile,
                pdfUrl: s.pdfUrl,
                pages: s.pages,
                selectedDesignSystemId: s.selectedDesignSystemId,
                source: s.source,
              },
            }),
      });
      s.markContinued();
      // The agent's runner owns a deck import from here, with its own progress
      // in the chat, so the dialog closes instead of showing step 3.
      if (isDeck) {
        closeImportDialog();
        return;
      }
      trackImport(
        "continue",
        {
          selectedPages: pages.length,
          themed: !!s.selectedDesignSystemId,
          concurrentImports: countActiveImports(
            useImportRegistry.getState().imports,
          ),
        },
        jobStore,
      );
      closeImportDialog();
    } finally {
      continuingRef.current = false;
    }
  }, [store]);

  const backToSource = useCallback(() => {
    abortRef.current?.abort();
    useImportRegistry.getState().setViewingId(null);
    store.getState().setStep("source");
  }, [store]);
  const backToPages = useCallback(() => {
    const viewing = getViewingImport();
    const upload = viewing?.store.getState().upload;
    if (upload) store.getState().restoreUpload(upload);
    else {
      store.getState().setReturnedFromVersions(true);
      store.getState().setStep("selecting");
    }
    viewing?.store.getState().setModalOpen(false);
  }, [store]);
  const abortUpload = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return {
    startUpload,
    startDriveUpload,
    continueToGeneration,
    backToSource,
    backToPages,
    cancel,
    abortUpload,
  };
};
